import { useFormContext, Controller } from 'react-hook-form'
import { ArrowRight } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MultiSelect } from '@/components/ui/multi-select'
import { useTechnologies } from '@/hooks'
import { PROJECT_TYPE_LABELS, PROJECT_INDUSTRY_LABELS } from '@/types'

const CLIENT_MAX = 256

interface ProjectTypeSectionProps {
  mode?: 'create' | 'edit'
}

export function ProjectTypeSection({ mode = 'create' }: ProjectTypeSectionProps) {
  const { control, register, watch, formState: { errors } } = useFormContext()
  const { data: technologies = [], isLoading: isLoadingTechnologies } = useTechnologies()

  const selectedType = watch('type')
  const isModernization = selectedType === 'Modernization'
  const clientValue = watch('client') ?? ''
  const industryValue = watch('industry') ?? ''
  const typeValue = watch('type') ?? ''
  const technologiesFromRaw = watch('technologiesFrom')
  const technologiesToRaw = watch('technologiesTo')
  const technologiesFromValue = Array.isArray(technologiesFromRaw) ? technologiesFromRaw : []
  const technologiesToValue = Array.isArray(technologiesToRaw) ? technologiesToRaw : []

  const isEditMode = mode === 'edit'

  const technologyOptions = technologies.map(t => ({
    value: t.value,
    label: t.label,
  }))

  const getTechnologyLabel = (value: string) => {
    const tech = technologies.find(t => t.value === value)
    return tech?.label ?? value
  }

  if (isEditMode) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="client">Client <span className="text-red-500">*</span></Label>
            <Input
              id="client"
              value={clientValue}
              disabled
              className="bg-muted text-muted-foreground cursor-not-allowed"
            />
          </div>
          <div className="space-y-2">
            <Label>Industry <span className="text-red-500">*</span></Label>
            <Input
              value={PROJECT_INDUSTRY_LABELS[industryValue] ?? industryValue}
              disabled
              className="bg-muted text-muted-foreground cursor-not-allowed"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_2fr]">
          <div className="space-y-2">
            <Label>Type <span className="text-red-500">*</span></Label>
            <Input
              value={PROJECT_TYPE_LABELS[typeValue] ?? typeValue}
              disabled
              className="bg-muted text-muted-foreground cursor-not-allowed"
            />
          </div>
          <div className="space-y-2">
            <Label>Technologies</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-1 px-3 border rounded-md bg-muted h-10 overflow-x-auto">
                {technologiesFromValue.length > 0 ? (
                  technologiesFromValue.map((tech: string) => (
                    <Badge key={tech} variant="secondary" className="text-muted-foreground shrink-0">
                      {getTechnologyLabel(tech)}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground text-sm">
                    {isModernization ? 'Not specified' : '-'}
                  </span>
                )}
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 flex items-center gap-1 px-3 border rounded-md bg-muted h-10 overflow-x-auto">
                {technologiesToValue.length > 0 ? (
                  technologiesToValue.map((tech: string) => (
                    <Badge key={tech} variant="secondary" className="text-muted-foreground shrink-0">
                      {getTechnologyLabel(tech)}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground text-sm">
                    {isModernization ? 'Not specified' : '-'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
        <div className="space-y-2">
          <div className="flex items-center justify-between min-h-[20px]">
            <Label htmlFor="client">Client <span className="text-red-500">*</span></Label>
            <span className="text-xs text-muted-foreground">{clientValue.trim().length}/{CLIENT_MAX}</span>
          </div>
          <Input
            id="client"
            placeholder="e.g. Qubiz"
            {...register('client', {
              required: 'Client is required',
              validate: (value) => {
                const trimmed = value.trim()
                if (trimmed.length === 0) return 'Client is required'
                if (trimmed.length > CLIENT_MAX) return `Client must be at most ${CLIENT_MAX} characters`
                return true
              },
            })}
          />
          {errors.client && (
            <p role="alert" className="text-sm text-destructive">{String(errors.client.message)}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center min-h-[20px]">
            <Label>Industry <span className="text-red-500">*</span></Label>
          </div>
          <Controller
            name="industry"
            control={control}
            rules={{ required: 'Project industry is required' }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PROJECT_INDUSTRY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.industry && (
            <p role="alert" className="text-sm text-destructive">{String(errors.industry.message)}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_2fr] md:items-start">
        <div className="space-y-2">
          <div className="flex items-center min-h-[20px]">
            <Label>Type <span className="text-red-500">*</span></Label>
          </div>
          <Controller
            name="type"
            control={control}
            rules={{ required: 'Project type is required' }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PROJECT_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.type && (
            <p role="alert" className="text-sm text-destructive">{String(errors.type.message)}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center min-h-[20px]">
            <Label>
              Technologies {isModernization && <span className="text-red-500">*</span>}
            </Label>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex-1 min-w-0">
              <Controller
                name="technologiesFrom"
                control={control}
                rules={{
                  validate: (value) => {
                    if (isModernization && (!value || value.length === 0)) {
                      return 'Technology From is required for Modernization projects'
                    }
                    return true
                  },
                }}
                render={({ field }) => (
                  <MultiSelect
                    options={technologyOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    placeholder={isLoadingTechnologies ? 'Loading...' : 'e.g. .NET'}
                    disabled={isLoadingTechnologies}
                  />
                )}
              />
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 self-center rotate-90 sm:rotate-0" />
            <div className="flex-1 min-w-0">
              <Controller
                name="technologiesTo"
                control={control}
                rules={{
                  validate: (value) => {
                    if (isModernization && (!value || value.length === 0)) {
                      return 'Technology To is required for Modernization projects'
                    }
                    return true
                  },
                }}
                render={({ field }) => (
                  <MultiSelect
                    options={technologyOptions}
                    value={field.value ?? []}
                    onChange={field.onChange}
                    placeholder={isLoadingTechnologies ? 'Loading...' : 'e.g. React'}
                    disabled={isLoadingTechnologies}
                  />
                )}
              />
            </div>
          </div>
          {(errors.technologiesFrom || errors.technologiesTo) && (
            <div className="flex flex-col gap-1 sm:flex-row sm:gap-2">
              <div className="flex-1">
                {errors.technologiesFrom && (
                  <p role="alert" className="text-sm text-destructive">{String(errors.technologiesFrom.message)}</p>
                )}
              </div>
              <div className="hidden sm:block w-4 shrink-0" />
              <div className="flex-1">
                {errors.technologiesTo && (
                  <p role="alert" className="text-sm text-destructive">{String(errors.technologiesTo.message)}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
